import { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TradeHistoryItem } from "../../interfaces/history";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { getPositionEffectParts } from "../../utils/trade";
import TradeHistoryDetailsModal from "./details/TradeHistoryDetailsModal";
import HistoryTableShell from "./HistoryTableShell";
import {
  buildActionsColumn,
  buildMarketColumn,
  buildPriceColumn,
  buildSideColumn,
  buildSignedNumberColumn,
  buildSizeColumn,
  buildTimestampColumn,
} from "./columns";
import { ExitReasonNote, PositionEffectTag } from "./style";
import {
  DecoratedFields,
  useDecoratedHistoryItems,
} from "./useDecoratedHistoryItems";

interface ITradeHistoryTableProps {
  items: TradeHistoryItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  height?: number;
}

type DecoratedTrade = TradeHistoryItem & DecoratedFields;

/**
 * Type cell. With an `exit_reason` the row reads "Close Long (Liquidated)";
 * without one it renders exactly as it always has.
 */
const renderPositionEffect = (
  effect: string | undefined,
  exitReason: string | undefined,
  t: TFunction
) => {
  const { base, reason } = getPositionEffectParts(effect, exitReason, t);
  if (!base && !reason) return "-";
  return (
    <PositionEffectTag effect={effect}>
      {base}
      {reason && <ExitReasonNote>({reason})</ExitReasonNote>}
    </PositionEffectTag>
  );
};

function TradeHistoryTable({
  items,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  height,
}: ITradeHistoryTableProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.TradeHistoryTable",
  });
  const { t: translateAll } = useTranslation();
  const { t: tooltips } = useTranslation("tooltips");
  const decorated = useDecoratedHistoryItems<TradeHistoryItem>(items);
  const [details, setDetails] = useState<DecoratedTrade>();

  const columns: ITableColumn<DecoratedTrade>[] = useMemo(() => {
    const ctx = { t, tooltips, translateAll };
    return [
      buildMarketColumn<DecoratedTrade>(ctx),
      buildSideColumn<DecoratedTrade>(ctx),
      {
        title: t("type", { defaultValue: "Type" }),
        accessor: "position_effect",
        tooltip: tooltips("positionEffect", {
          defaultValue:
            "Effect of this trade on the position (open long / open short / close long / close short).",
        }),
        Cell: ({ value, row }) =>
          renderPositionEffect(
            value as string | undefined,
            row.original.exit_reason,
            t
          ),
      },
      buildSizeColumn<DecoratedTrade>(ctx, { priceAccessor: "price" }),
      buildPriceColumn<DecoratedTrade>(ctx, { accessor: "price" }),
      buildSignedNumberColumn<DecoratedTrade>(ctx, {
        accessor: "total_fees",
        titleKey: "fees",
        tooltipKey: "tradeHistoryFee",
        invert: true,
      }),
      buildTimestampColumn<DecoratedTrade>(ctx, {
        accessor: "created_timestamp",
        unit: "nanos",
      }),
      buildActionsColumn<DecoratedTrade>(
        "trade_id",
        (row) => setDetails(row),
        "tx_hash"
      ),
    ];
  }, [t, tooltips, translateAll]);

  return (
    <>
      <TradeHistoryDetailsModal
        trade={details}
        onHide={() => setDetails(undefined)}
      />
      <HistoryTableShell<DecoratedTrade>
        columns={columns}
        items={decorated}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        onRowClick={(row) => setDetails(row)}
        emptyText={t("no_trade_history", {
          defaultValue: "No trade history yet.",
        })}
        height={height}
      />
    </>
  );
}

export default TradeHistoryTable;
